/**
 * Header Icon Theme
 *
 * Detects what's actually rendered behind the sticky header and toggles
 * a class on the <sticky-header> element so the menu + cart icons can
 * be styled to contrast with the background:
 *   - light background  → adds .header--on-light  → icons should be dark
 *   - dark  background  → adds .header--on-dark   → icons should be light
 *
 * Strategy: on each scroll/resize, sample the elements at a point just
 * inside the header, ignore the header itself, walk up to find the first
 * opaque background-color, compute its luminance, decide the class.
 */
(function () {
  class HeaderIconTheme {
    constructor() {
      this.header =
        document.querySelector('sticky-header') ||
        document.querySelector('.header-wrapper');
      if (!this.header) return;

      this.scheduled = false;
      this.scheduleUpdate = this.scheduleUpdate.bind(this);
      this.update = this.update.bind(this);

      window.addEventListener('scroll', this.scheduleUpdate, { passive: true });
      window.addEventListener('resize', this.scheduleUpdate);
      // Run after a tick so layout has settled.
      requestAnimationFrame(this.update);
    }

    scheduleUpdate() {
      if (this.scheduled) return;
      this.scheduled = true;
      requestAnimationFrame(() => {
        this.scheduled = false;
        this.update();
      });
    }

    update() {
      const rect = this.header.getBoundingClientRect();
      if (rect.height < 1) return;

      // Sample one point on each side where the icons actually sit, plus
      // the center. Average their luminance so a logo image in the middle
      // doesn't throw off the read for the side icons.
      const y = rect.top + rect.height / 2;
      const samplePoints = [
        { x: Math.max(rect.left + 30, 1), y },
        { x: window.innerWidth / 2, y },
        { x: Math.min(rect.right - 30, window.innerWidth - 1), y }
      ];

      let total = 0;
      let count = 0;
      for (const point of samplePoints) {
        const lum = this.luminanceAt(point.x, point.y);
        if (lum !== null) {
          total += lum;
          count += 1;
        }
      }
      if (count === 0) return;

      const avg = total / count;
      const isLight = avg > 140; // 0–255 scale

      this.header.classList.toggle('header--on-light', isLight);
      this.header.classList.toggle('header--on-dark', !isLight);
    }

    luminanceAt(x, y) {
      const stack = document.elementsFromPoint(x, y);
      // Find first element that is NOT the header (or inside it).
      const target = stack.find(
        (el) => el !== this.header && !this.header.contains(el)
      );
      if (!target) return null;

      const bg = this.firstOpaqueBackground(target);
      if (!bg) return null;
      return this.luminance(bg);
    }

    firstOpaqueBackground(el) {
      let cur = el;
      while (cur && cur !== document.documentElement) {
        const style = window.getComputedStyle(cur);
        const bg = style.backgroundColor;
        if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
          // Only treat as opaque if alpha is meaningful.
          const m = bg.match(/rgba?\(([^)]+)\)/);
          if (m) {
            const parts = m[1].split(',').map((s) => parseFloat(s.trim()));
            const alpha = parts.length === 4 ? parts[3] : 1;
            if (alpha >= 0.5) return bg;
          } else {
            return bg;
          }
        }
        cur = cur.parentElement;
      }
      // Fall back to body / html background.
      const bodyBg = window.getComputedStyle(document.body).backgroundColor;
      if (bodyBg && bodyBg !== 'rgba(0, 0, 0, 0)') return bodyBg;
      return 'rgb(255, 255, 255)';
    }

    luminance(color) {
      const m = color.match(/\d+(\.\d+)?/g);
      if (!m || m.length < 3) return 255;
      const [r, g, b] = m.slice(0, 3).map(Number);
      // Rec. 601 luma — good enough for "is this background bright or dark".
      return r * 0.299 + g * 0.587 + b * 0.114;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new HeaderIconTheme());
  } else {
    new HeaderIconTheme();
  }
})();
